import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchSayingById, fetchTranslations, fetchLanguages, createTranslation, updateTranslation, deleteTranslation, testConnection } from '../api';
import { Button } from '../components/ui/button';
import { Pencil, Trash2, Plus, X } from 'lucide-react';

function AdminSayingDetailPage() {
  const { sayingId } = useParams();
  const navigate = useNavigate();
  const [saying, setSaying] = useState(null);
  const [translations, setTranslations] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editForm, setEditForm] = useState({
    translated_text: '',
    explanation: '',
    language_id: ''
  });
  const [addForm, setAddForm] = useState({
    translated_text: '',
    explanation: '',
    language_id: ''
  });

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Test connection first
      const isConnected = await testConnection();
      if (!isConnected) {
        throw new Error('Cannot connect to backend server. Please ensure the backend is running on http://localhost:8000');
      }
      
      const [sayingData, languagesData] = await Promise.all([
        fetchSayingById(sayingId),
        fetchLanguages()
      ]);
      setSaying(sayingData);
      setLanguages(languagesData);

      // Fetch all translations for this saying
      const translationsData = await fetchTranslations({ original_saying_id: sayingId });
      setTranslations(translationsData);
      setError(null);
    } catch (err) {
      console.error('Error loading saying details:', err);
      setError('Failed to load saying details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [sayingId]);

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!addForm.translated_text.trim() || !addForm.language_id) {
      setError('Please fill in all required fields (translation text and language)');
      return;
    }
    
    console.log('Submitting translation:', {
      original_saying_id: parseInt(sayingId),
      translated_text: addForm.translated_text,
      explanation: addForm.explanation,
      language_id: parseInt(addForm.language_id)
    });
    
    try {
      const result = await createTranslation({
        original_saying_id: parseInt(sayingId),
        translated_text: addForm.translated_text,
        explanation: addForm.explanation,
        language_id: parseInt(addForm.language_id)
      });
      console.log('Translation created successfully:', result);
      setAddForm({ translated_text: '', explanation: '', language_id: '' });
      setShowAddForm(false);
      setError(null); // Clear any previous errors
      await loadData();
    } catch (error) {
      console.error('Error creating translation:', error);
      setError(`Failed to create translation: ${error.message}`);
    }
  };

  const startEdit = (translation) => {
    setEditingId(translation.translation_id);
    setEditForm({
      translated_text: translation.translated_text,
      explanation: translation.explanation || '',
      language_id: translation.language_id
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!editForm.translated_text.trim() || !editForm.language_id) {
      setError('Please fill in all required fields (translation text and language)');
      return;
    }
    
    console.log('Updating translation:', editingId, {
      original_saying_id: parseInt(sayingId),
      translated_text: editForm.translated_text,
      explanation: editForm.explanation,
      language_id: parseInt(editForm.language_id)
    });
    
    try {
      const result = await updateTranslation(editingId, {
        original_saying_id: parseInt(sayingId),
        translated_text: editForm.translated_text,
        explanation: editForm.explanation,
        language_id: parseInt(editForm.language_id)
      });
      console.log('Translation updated successfully:', result);
      setEditingId(null);
      setError(null); // Clear any previous errors
      await loadData();
    } catch (error) {
      console.error('Error updating translation:', error);
      setError(`Failed to update translation: ${error.message}`);
    }
  };

  const handleDelete = async (translationId) => {
    if (!window.confirm('Are you sure you want to delete this translation?')) return;
    try {
      await deleteTranslation(translationId);
      await loadData();
    } catch (error) {
      console.error('Error deleting translation:', error);
      setError(`Failed to delete translation: ${error.message}`);
    }
  };

  const getAvailableLanguages = () => {
    const usedLanguageIds = translations.map(t => t.language_id);
    // Don't include the original saying's language either
    const excludedIds = [...usedLanguageIds, saying?.language_id];
    return languages.filter(lang => !excludedIds.includes(lang.language_id));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4">
        <div className="text-center text-destructive mb-4">{error}</div>
        <Button onClick={() => navigate('/sayings')} variant="outline">
          ← Back to Admin Sayings
        </Button>
      </div>
    );
  }

  if (!saying) {
    return (
      <div className="container mx-auto p-4">
        <div className="text-center mb-4">Saying not found</div>
        <Button onClick={() => navigate('/sayings')} variant="outline">
          ← Back to Admin Sayings
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 space-y-6">
      <Button onClick={() => navigate('/sayings')} variant="outline" className="mb-4">
        ← Back to Admin Sayings
      </Button>

      {/* Error Display */}
      {error && (
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg border border-destructive/20">
          <div className="flex justify-between items-start">
            <p>{error}</p>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setError(null)}
              className="text-destructive hover:text-destructive"
            >
              ×
            </Button>
          </div>
        </div>
      )}

      {/* Original Saying */}
      <div className="bg-card p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold mb-2">Original Saying</h2>
        <p className="text-lg mb-4">{saying.saying}</p>
        <div className="text-sm text-muted-foreground">
          <p>Category: {saying.category_name}</p>
          <p>Language: {languages.find(l => l.language_id === saying.language_id)?.name}</p>
          <p>Approved: {saying.approved ? <span className="text-green-600 font-semibold">Yes</span> : <span className="text-destructive font-semibold">No</span>}</p>
        </div>
      </div>

      {/* Translations Management Section */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-xl font-semibold">Translations ({translations.length})</h3>
          <Button 
            onClick={() => setShowAddForm(true)} 
            disabled={getAvailableLanguages().length === 0}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Translation
          </Button>
        </div>

        {/* Add Translation Form */}
        {showAddForm && (
          <div className="bg-card p-6 rounded-lg border-2 border-primary">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg font-semibold">Add New Translation</h4>
              <Button variant="ghost" size="sm" onClick={() => setShowAddForm(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <form onSubmit={handleAddSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Language</label>
                <select
                  value={addForm.language_id}
                  onChange={(e) => setAddForm({ ...addForm, language_id: e.target.value })}
                  className="w-full px-3 py-2 rounded-md border border-input bg-background"
                  required
                >
                  <option value="">Select Language</option>
                  {getAvailableLanguages().map(lang => (
                    <option key={lang.language_id} value={lang.language_id}>
                      {lang.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Translation</label>
                <textarea
                  value={addForm.translated_text}
                  onChange={(e) => setAddForm({ ...addForm, translated_text: e.target.value })}
                  className="w-full px-3 py-2 rounded-md border border-input bg-background h-24"
                  placeholder="Enter translation..."
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Explanation (Optional)</label>
                <textarea
                  value={addForm.explanation}
                  onChange={(e) => setAddForm({ ...addForm, explanation: e.target.value })}
                  className="w-full px-3 py-2 rounded-md border border-input bg-background h-20"
                  placeholder="Enter explanation..."
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit">Add Translation</Button>
                <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Translations List */}
        {translations.length === 0 ? (
          <div className="text-center text-muted-foreground py-8 bg-card rounded-lg">
            <p>No translations available yet.</p>
            {getAvailableLanguages().length > 0 && (
              <p className="mt-2">Click "Add Translation" to create the first one.</p>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            {translations.map((translation) => {
              const language = languages.find(l => l.language_id === translation.language_id);
              return (
                <div
                  key={translation.translation_id}
                  className="bg-card p-4 rounded-lg border shadow-sm"
                >
                  {editingId === translation.translation_id ? (
                    <form onSubmit={handleEditSubmit} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Language</label>
                        <select
                          value={editForm.language_id}
                          onChange={(e) => setEditForm({ ...editForm, language_id: e.target.value })}
                          className="w-full px-3 py-2 rounded-md border border-input bg-background"
                          required
                        >
                          <option value={translation.language_id}>
                            {language?.name}
                          </option>
                          {getAvailableLanguages().map(lang => (
                            <option key={lang.language_id} value={lang.language_id}>
                              {lang.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Translation</label>
                        <textarea
                          value={editForm.translated_text}
                          onChange={(e) => setEditForm({ ...editForm, translated_text: e.target.value })}
                          className="w-full px-3 py-2 rounded-md border border-input bg-background h-24"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Explanation</label>
                        <textarea
                          value={editForm.explanation}
                          onChange={(e) => setEditForm({ ...editForm, explanation: e.target.value })}
                          className="w-full px-3 py-2 rounded-md border border-input bg-background h-20"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button type="submit" size="sm">Save</Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => setEditingId(null)}>
                          Cancel
                        </Button>
                      </div>
                    </form>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="inline-block px-2 py-1 bg-primary/10 text-primary rounded text-sm font-medium">
                              {language?.name}
                            </span>
                          </div>
                          <p className="text-lg mb-2">{translation.translated_text}</p>
                          {translation.explanation && (
                            <p className="text-sm text-muted-foreground italic">
                              <strong>Explanation:</strong> {translation.explanation}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => startEdit(translation)}
                            title="Edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(translation.translation_id)}
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {getAvailableLanguages().length === 0 && !showAddForm && (
          <div className="text-center text-muted-foreground py-4 bg-card rounded-lg border-2 border-dashed">
            <p>All available languages have been translated.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminSayingDetailPage; 